import React, { ComponentProps, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { GridAlignment, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { PATIENT_INITIALS_FEATURE_FLAG } from 'featureFlags';
import { useSuppliers } from 'api/hooks/useSuppliers';
import { DeliverySummary } from 'api/models/Deliveries';
import Theme from 'App.theme';
import { TableTypes } from 'pages/ActionItems/types';
import DeliveryContentsText from 'components/DeliveryContentsText/DeliveryContentsText';
import { DropTableContainer } from 'components/DeliveryListPage/styles';
import DeliveryPriorityLabels from 'components/DeliveryPriorityLabels';
import Pagination from 'components/Pagination';
import { StyledDataGrid } from 'components/StyledDataGrid/StyledDataGrid';
import { useLanguage } from 'context/AppContext';
import useScreenSize from 'hooks/useScreenSize';
import useSupplierLabels from 'hooks/useSupplierLabels';
import { formatDate, removeTimeInfoFromDate } from 'utils/helpers/DateTimeFormat.helpers';
import { DeliveryStatusChip } from '../../../DeliveryStatusChip/DeliveryStatusChip';
import highlightRed from '../../highlightRed';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { TenantType } from 'api/models/Users';

export const formatSurgeryDate = (date: string | Date) => formatDate(removeTimeInfoFromDate(date.toString()), false);

// Renders a formatted datetime (dd/MM/yyyy - hh:mm a) with the date and time on separate lines to save table width.
const renderDateTimeCell = (value: string) => {
  const [datePart, timePart] = value.split(' - ');
  return (
    <div style={{ lineHeight: '1.2', textAlign: 'center' }}>
      <div>{datePart}</div>
      {timePart ? <div>{timePart}</div> : null}
    </div>
  );
};

type DeliveryListProps = {
  isActionItemsTable: boolean;
  data: DeliverySummary[];
  tableType?: TableTypes;
  numRows?: number;
  numPages: number;
  curPage: number;
  summary?: string;
  pageSizeOptions?: number[];
  pageSize: number;
  loading?: boolean;
  isPastDeliveries?: boolean;
  onSortModelChange?: (model: GridSortModel) => void;
  onPageSizeChange?: (pageSizeChangeModel: GridPaginationModel) => void;
  onRowClick: (deliveryId: number) => void;
} & ComponentProps<typeof Box>;

type RowProps = {
  id: number;
  supplier: string;
  deliveryNumber: string;
  shipmentId: string;
  eta: string;
  inspectionStatus: JSX.Element | null;
  contents: JSX.Element | null;
  clinicalArea: string;
  patientInitials: string;
  surgeryReference: string;
  surgeon?: string | null;
  express: boolean;
  turnaroundOrigin: boolean;
  turnaroundDestination: boolean;
  selectedRateName?: string;
  highlightRed: boolean;
  surgeryDate: string;
  dueDate: string;
};

const DeliveryList: React.FC<DeliveryListProps> = ({
  isActionItemsTable,
  data,
  tableType,
  numRows,
  numPages,
  curPage,
  summary,
  pageSizeOptions: passedPageSizeOptions,
  pageSize,
  loading,
  isPastDeliveries = false,
  onSortModelChange,
  onPageSizeChange,
  onRowClick,
  sx,
  ...props
}) => {
  const [langs] = useLanguage();
  const lang = useMemo(
    () => ({
      ...langs.DeliveryListPage,
      checkIn: langs.Routes.CheckIn,
      checkOut: langs.Routes.CheckOut,
    }),
    [langs.DeliveryListPage, langs.Routes.CheckIn, langs.Routes.CheckOut],
  );

  const { data: user } = useCurrentUser();
  const isSupplierUser = user?.tenant?.type === TenantType.SUPPLIER;

  const screenSize = useScreenSize();

  const tableEl = useRef<HTMLDivElement>(null);

  const isSortable = isActionItemsTable ? false : true;

  const { data: suppliers } = useSuppliers();
  const supplierLabels = useSupplierLabels({ suppliers: suppliers?.suppliers });

  const CustomHeader = ({ colDef }) => {
    return (
      <div
        style={{
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          textAlign: 'center',
          lineHeight: '1.2',
          margin: '0',
        }}
      >
        {colDef.headerName}
      </div>
    );
  };

  const memoizedRows = useMemo(() => {
    return data.map(delivery => {
      const formattedDueDate = formatDate(removeTimeInfoFromDate(delivery.dueDate), false);

      const row: RowProps = {
        id: delivery.deliveryId,
        supplier: supplierLabels[delivery.supplier] || delivery.supplier,
        deliveryNumber: delivery.deliveryNumber,
        shipmentId: delivery.shipmentId || '',
        eta: delivery.etaInfo?.orderEta ? formatDate(delivery.etaInfo.orderEta, true) : '',
        express: delivery.express,
        turnaroundOrigin: delivery.turnaroundOrigin || false,
        turnaroundDestination: delivery.turnaroundDestination || false,
        selectedRateName: delivery.selectedRate?.name,
        inspectionStatus: <DeliveryStatusChip status={delivery.status} allowWrap />,
        contents: <DeliveryContentsText variant="text-only" contents={delivery.content} showNotSpecifiedText={false} />,
        clinicalArea: delivery.clinicalArea,
        surgeryReference: delivery.surgeryReference,
        surgeon: delivery.surgeon,
        patientInitials: delivery.patientInitials || '',
        surgeryDate: formatSurgeryDate(delivery.surgeryDate),
        dueDate: formattedDueDate,
        highlightRed: isPastDeliveries
          ? false
          : highlightRed(
              delivery.surgeryDate,
              delivery.express,
              delivery.status,
              delivery.selectedRate?.name,
              tableType,
            ),
      };

      return row;
    });
  }, [data, supplierLabels, isPastDeliveries, tableType]);

  const memoizedColumns = useMemo(() => {
    const columns: GridColDef[] = [
      {
        field: 'inspectionStatus',
        headerName: lang.table.inspectionStatus,
        align: 'center',
        width: 140,
        headerAlign: 'center',
        sortable: isSortable,
        renderHeader: params => <CustomHeader colDef={params.colDef} />,
        renderCell: params => {
          return params.row.inspectionStatus;
        },
      },
      {
        field: 'eta',
        headerName: lang.table.eta,
        align: 'center',
        headerAlign: 'center',
        minWidth: 88,
        sortable: isSortable,
        renderHeader: params => <CustomHeader colDef={params.colDef} />,
        renderCell: params => renderDateTimeCell(params.row.eta),
      },
      {
        field: 'shipmentId',
        headerName: lang.table.shipmentId,
        align: 'center',
        headerAlign: 'center',
        //minWidth: 110,
        sortable: isSortable,
        renderHeader: params => <CustomHeader colDef={params.colDef} />,
      },
      {
        field: 'surgeryDate',
        headerName: lang.table.surgeryDate,
        align: 'center',
        headerAlign: 'center',
        minWidth: 88,
        sortable: isSortable,
        renderHeader: params => <CustomHeader colDef={params.colDef} />,
        renderCell: params => renderDateTimeCell(params.row.surgeryDate),
        flex: isPastDeliveries ? 0 : 1,
      },
      {
        field: 'surgeon',
        headerName: lang.table.surgeon,
        headerAlign: 'center',
        minWidth: 110,
        align: 'center',
        sortable: isSortable,
        renderHeader: params => <CustomHeader colDef={params.colDef} />,
      },
      ...(PATIENT_INITIALS_FEATURE_FLAG
        ? [
            {
              field: 'patientInitials',
              headerName: lang.table.patientInitials,
              headerAlign: 'center' as GridAlignment,
              //minWidth: 110,
              align: 'center' as GridAlignment,
              sortable: isSortable,
              renderHeader: params => <CustomHeader colDef={params.colDef} />,
            },
          ]
        : []),
      {
        field: 'supplier',
        headerName: lang.table.supplier,
        sortable: isSortable,
        headerAlign: 'center',
        minWidth: 110,
        align: 'center',
        renderHeader: params => <CustomHeader colDef={params.colDef} />,
        renderCell: params => (
          <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-all', textAlign: 'center' }}>{params.value}</div>
        ),
      },
      {
        field: 'surgeryReference',
        headerName: lang.table.surgeryReference,
        align: 'center',
        headerAlign: 'center',
        minWidth: 110,
        sortable: isSortable,
        renderHeader: params => <CustomHeader colDef={params.colDef} />,
        flex: 1,
      },

      ...(isSupplierUser
        ? [
            {
              field: 'deliveryNumber',
              headerName: lang.table.deliveryNumber,
              sortable: isSortable,
              align: 'center' as GridAlignment,
              minWidth: screenSize.width < Theme.breakpoints.values.xl ? 100 : 150,
              headerAlign: 'center' as GridAlignment,
              flex: screenSize.width < Theme.breakpoints.values.xl ? 1 : undefined, // Apply flex: 1 when screen size is below xl
              renderHeader: params => <CustomHeader colDef={params.colDef} />,
              renderCell: params => (
                <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-all', textAlign: 'center' }}>
                  {params.value}
                </div>
              ),
            } as GridColDef,
          ]
        : []),
      {
        field: 'priorityRequests',
        headerName: lang.table.priorityRequests,
        align: 'center',
        headerAlign: 'center',
        minWidth: 100,
        sortable: false,
        flex: 1,
        renderHeader: params => <CustomHeader colDef={params.colDef} />,
        renderCell: params => (
          <DeliveryPriorityLabels
            express={params.row.express}
            turnaroundOrigin={params.row.turnaroundOrigin}
            turnaroundDestination={params.row.turnaroundDestination}
            selectedRateName={params.row.selectedRateName}
          />
        ),
      },
      {
        field: 'dueDate',
        headerName: lang.table.dueDate.label,
        align: 'center',
        minWidth: 88,
        headerAlign: 'center',
        flex: 1,
        renderHeader: params => (
          <>
            {isPastDeliveries ? (
              <div
                style={{
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  textAlign: 'center',
                  lineHeight: '1.2',
                  margin: '0',
                }}
              >
                {lang.table.returnDate}
              </div>
            ) : (
              <CustomHeader colDef={params.colDef} />
            )}
          </>
        ),
        renderCell: params => renderDateTimeCell(params.row.dueDate),
        sortable: isSortable,
      },
      {
        field: 'contents',
        headerName: lang.table.contents,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        width: 100,
        renderHeader: params => <CustomHeader colDef={params.colDef} />,
        renderCell: params => {
          return params.row.contents;
        },
      },
    ];

    return columns;
  }, [
    lang.table.inspectionStatus,
    lang.table.eta,
    lang.table.shipmentId,
    lang.table.surgeryDate,
    lang.table.surgeon,
    lang.table.patientInitials,
    lang.table.supplier,
    lang.table.surgeryReference,
    lang.table.deliveryNumber,
    lang.table.priorityRequests,
    lang.table.dueDate,
    lang.table.contents,
    lang.table.returnDate,
    isSortable,
    isPastDeliveries,
    isSupplierUser,
    screenSize.width,
  ]);

  const pageSizeOptions = useMemo(() => passedPageSizeOptions || [25, 50, 100], [passedPageSizeOptions]);

  return (
    <Box className="page-content" {...props} sx={{ ...sx, display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Box
        sx={{
          transition: 'max-height 0.3s ease',
          //height: '100%',
          flex: 1,
          minHeight: 0,
        }}
      >
        <DropTableContainer ref={tableEl}>
          <StyledDataGrid
            stickyHeader
            hidePagination={isActionItemsTable}
            pageSizeOptions={pageSizeOptions}
            paginationMode={numRows == null ? 'client' : 'server'}
            disableVirtualization={process.env.NODE_ENV == 'test'}
            paginationModel={{
              pageSize,
              // DataGrid expects a 0 based index of the page and a value not less than 0
              page: curPage > 0 ? curPage - 1 : 0,
            }}
            onRowClick={params => onRowClick(params.row.id)}
            rowCount={numRows == null ? memoizedRows.length : numRows}
            pagination
            disableColumnMenu
            rows={memoizedRows}
            rowHeight={Theme.sizes.rowHeight}
            sortingMode="server"
            columns={memoizedColumns}
            disableRowSelectionOnClick
            loading={loading}
            onSortModelChange={onSortModelChange} // Capture sort model changes
            getRowClassName={params => (params.row.highlightRed ? 'red-highlight' : '')}
            onPaginationModelChange={onPageSizeChange}
          />
        </DropTableContainer>
      </Box>
      {!loading && !isActionItemsTable && (
        <>
          <Box sx={{ mb: Theme.layout.spacing.medium }} />

          <Pagination id="DeliveryListTable" numPages={numPages} curPage={curPage} />
          {summary != '' && screenSize.width >= Theme.breakpoints.values.md && (
            <Box
              sx={{
                marginTop: '-48px',
                fontSize: '14px',
                color: Theme.greys.GREY1,
                marginBottom: Theme.layout.spacing.medium,
                padding: '8px',
              }}
            >
              {summary}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DeliveryList;
